import { useMemo, useState } from "react";
import { Card, CardContent, Typography, Box, Grid, Popover } from '@mui/material';
import { AppCard } from '../../components/common/AppCard';
import BarChartIcon from "@mui/icons-material/BarChart";
import InventoryIcon from "@mui/icons-material/Inventory2";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { AssetDistributionChart, PortfolioChart } from './components';
import { AccountPortfolio, AccountTrade, AccountTransfer, calculateTradingAnalysis } from '../../types';
import { AnimatedValue } from '../../components/AnimatedValue';
import { resolveValuation } from '../../utils/resolveValuation';

interface Props {
    portfolios: AccountPortfolio[];
    trades: AccountTrade[];
    transfers: AccountTransfer[];
    fromDate: Date;
    toDate: Date;
    currency: "eur" | "usd" | "chf";
    liveTrigger?: unknown;
    isCombined?: boolean;
    accountIds?: string[];
    compositeAccountIds?: string[];
    isLive?: boolean;
    hasPartialData?: boolean;
    partialDataTooltip?: string;
}

export const PortfolioOverview = ({
    portfolios, trades, transfers, currency, fromDate, toDate,
    liveTrigger, isCombined, accountIds, compositeAccountIds, isLive, hasPartialData, partialDataTooltip,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isPinned, setIsPinned] = useState(false);
    const [realizedAnchorEl, setRealizedAnchorEl] = useState<HTMLElement | null>(null);
    const [unrealizedAnchorEl, setUnrealizedAnchorEl] = useState<HTMLElement | null>(null);

    // get latest portfolio snapshot
    const latestPortfolio = portfolios.length > 0
        ? portfolios[portfolios.length - 1]
        : null;

    const firstPortfolio = portfolios[0] ?? null;

    // Trade-based PnL calculation
    const allAccountIds = useMemo(() => {
        const primary = latestPortfolio?.account_id;
        if (!primary) return undefined;
        return compositeAccountIds?.length ? [primary, ...compositeAccountIds] : [primary];
    }, [latestPortfolio?.account_id, compositeAccountIds]);

    const analysis = useMemo(
        () => calculateTradingAnalysis(trades, transfers, firstPortfolio, latestPortfolio, currency, { from: fromDate, to: toDate }, allAccountIds),
        [trades, transfers, firstPortfolio, latestPortfolio, currency, fromDate, toDate, allAccountIds]
    );

    // Transfer summaries for unrealized P&L popover. Hooks stay above the early
    // return below: it fires on an empty account until the first live snapshot.
    const transferredIn = useMemo(() =>
        transfers.filter(t => allAccountIds?.includes(t.target) && !allAccountIds?.includes(t.source))
            .reduce((sum, t) => sum + (resolveValuation(t.valuation, currency) ?? 0), 0),
        [transfers, allAccountIds, currency]
    );
    const transferredOut = useMemo(() =>
        transfers.filter(t => allAccountIds?.includes(t.source) && !allAccountIds?.includes(t.target))
            .reduce((sum, t) => sum + (resolveValuation(t.valuation, currency) ?? 0), 0),
        [transfers, allAccountIds, currency]
    );

    if (!latestPortfolio) {
        return (
            <AppCard>
                <CardContent sx={{ "&:last-child": { paddingBottom: "16px" } }}>
                    <Typography variant="subtitle1" color="textSecondary">
                        No portfolio data available.
                    </Typography>
                </CardContent>
            </AppCard>
        );
    }

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        if (!isPinned) {
            setAnchorEl(null);
        }
    };

    const handleTogglePin = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setIsPinned(!isPinned);
        if (!isPinned) {
            setAnchorEl(event.currentTarget);
        } else {
            setAnchorEl(null);
        }
    };

    const handleClickAway = () => {
        setIsPinned(false);
        setAnchorEl(null);
    };

    const { valuation } = latestPortfolio;

    const rawValue = resolveValuation(valuation, currency);
    const value = rawValue ?? 0;
    const hasValuation = rawValue != null;

    const realizedPnL = analysis.totalRealizedPnL - analysis.totalCosts;
    const unrealizedPnL = analysis.totalUnrealizedPnL;

    const formatCurrency = (v: number) =>
        `${v >= 0 ? "+" : ""}${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`;

    return (
        <AppCard>
            <CardContent sx={{ p: 0, "&:last-child": { paddingBottom: 0 } }}>
                {/* Portfolio Summary */}
                <Box sx={{ backgroundColor: "action.hover", p: 2 }}>
                    <Grid container spacing={3} alignItems="stretch">
                        {/* Portfolio Value */}
                        <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                            <Box display="flex" flexDirection="column" gap={1} width="fit-content">
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <BarChartIcon fontSize="small" />
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Portfolio Value
                                    </Typography>
                                </Box>
                                {hasValuation ? (
                                    <AnimatedValue
                                        value={value}
                                        format={(v) => `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`}
                                        trigger={liveTrigger}
                                        variant="h4"
                                        fontWeight="bold"
                                        align="center"
                                    />
                                ) : (
                                    <Typography variant="h4" fontWeight="bold" align="center" color="text.disabled">
                                        N/A
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        {/* Performance Metrics */}
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={3}>
                                {/* Assets */}
                                <Grid item xs={6} sm={4}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                        onMouseEnter={handlePopoverOpen}
                                        onMouseLeave={handlePopoverClose}
                                        onClick={handleTogglePin}
                                        sx={{
                                            cursor: "pointer",
                                            p: 1,
                                            borderRadius: 1,
                                            transition: "background-color 0.2s ease",
                                            backgroundColor: isPinned ? "action.selected" : "transparent",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <InventoryIcon fontSize="small" />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Assets
                                            </Typography>
                                        </Box>
                                        <Typography variant="h6" fontWeight="bold">
                                            {latestPortfolio.asset_data?.balances
                                                ? Object.keys(latestPortfolio.asset_data.balances).length
                                                : 0}
                                        </Typography>
                                        <Popover
                                            id="assets-popover"
                                            open={Boolean(anchorEl)}
                                            anchorEl={anchorEl}
                                            onClose={handleClickAway}
                                            anchorOrigin={{
                                                vertical: "bottom",
                                                horizontal: "center",
                                            }}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "center",
                                            }}
                                            BackdropProps={{
                                                sx: { backgroundColor: "transparent" }
                                            }}
                                            sx={{ pointerEvents: "none" }}
                                            PaperProps={{
                                                sx: { pointerEvents: "auto" }
                                            }}
                                        >
                                            <Card sx={{ p: 0, width: 350, height: 350 }}>
                                                <AssetDistributionChart
                                                    portfolio={latestPortfolio}
                                                    width={350}
                                                    height={350}
                                                />
                                            </Card>
                                        </Popover>
                                    </Box>
                                </Grid>

                                {/* Realized P&L */}
                                <Grid item xs={6} sm={4}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                        onMouseEnter={(e) => setRealizedAnchorEl(e.currentTarget)}
                                        onMouseLeave={() => setRealizedAnchorEl(null)}
                                        sx={{
                                            cursor: "default",
                                            p: 1,
                                            borderRadius: 1,
                                            transition: "background-color 0.2s ease",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <TrendingUpIcon fontSize="small" />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Realized P&L
                                            </Typography>
                                        </Box>
                                        {hasValuation ? (
                                            <AnimatedValue
                                                value={realizedPnL}
                                                trigger={liveTrigger}
                                                format={formatCurrency}
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ color: realizedPnL >= 0 ? "success.main" : "error.main" }}
                                            />
                                        ) : (
                                            <Typography variant="h6" fontWeight="bold" color="text.disabled">
                                                N/A
                                            </Typography>
                                        )}
                                        <Popover
                                            id="realized-popover"
                                            open={Boolean(realizedAnchorEl)}
                                            anchorEl={realizedAnchorEl}
                                            onClose={() => setRealizedAnchorEl(null)}
                                            anchorOrigin={{
                                                vertical: "bottom",
                                                horizontal: "center",
                                            }}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "center",
                                            }}
                                            disableRestoreFocus
                                            sx={{ pointerEvents: "none" }}
                                            PaperProps={{
                                                sx: { pointerEvents: "auto" }
                                            }}
                                        >
                                            <Card sx={{ p: 2, width: 250 }}>
                                                <Grid container direction="column" spacing={1}>
                                                    <Grid item container>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Gross P&L:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} textAlign="right">
                                                            <Typography variant="body2" sx={{ color: analysis.totalRealizedPnL >= 0 ? "success.main" : "error.main" }}>
                                                                {formatCurrency(analysis.totalRealizedPnL)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item container>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Costs:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} textAlign="right">
                                                            <Typography variant="body2" color="error.main">
                                                                {formatCurrency(-analysis.totalCosts)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item><Box sx={{ borderTop: 1, borderColor: "divider" }} /></Grid>
                                                    <Grid item container>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                Net P&L:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} textAlign="right">
                                                            <Typography variant="body2" fontWeight="bold" sx={{ color: realizedPnL >= 0 ? "success.main" : "error.main" }}>
                                                                {formatCurrency(realizedPnL)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Card>
                                        </Popover>
                                    </Box>
                                </Grid>

                                {/* Unrealized P&L */}
                                <Grid item xs={6} sm={4}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                        onMouseEnter={(e) => setUnrealizedAnchorEl(e.currentTarget)}
                                        onMouseLeave={() => setUnrealizedAnchorEl(null)}
                                        sx={{
                                            cursor: "default",
                                            p: 1,
                                            borderRadius: 1,
                                            transition: "background-color 0.2s ease",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <ShowChartIcon fontSize="small" />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Unrealized P&L
                                            </Typography>
                                        </Box>
                                        {hasValuation ? (
                                            <AnimatedValue
                                                value={unrealizedPnL}
                                                trigger={liveTrigger}
                                                format={formatCurrency}
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ color: unrealizedPnL >= 0 ? "success.main" : "error.main" }}
                                            />
                                        ) : (
                                            <Typography variant="h6" fontWeight="bold" color="text.disabled">
                                                N/A
                                            </Typography>
                                        )}
                                        <Popover
                                            id="unrealized-popover"
                                            open={Boolean(unrealizedAnchorEl)}
                                            anchorEl={unrealizedAnchorEl}
                                            onClose={() => setUnrealizedAnchorEl(null)}
                                            anchorOrigin={{
                                                vertical: "bottom",
                                                horizontal: "center",
                                            }}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "center",
                                            }}
                                            disableRestoreFocus
                                            sx={{ pointerEvents: "none" }}
                                            PaperProps={{
                                                sx: { pointerEvents: "auto" }
                                            }}
                                        >
                                            <Card sx={{ p: 2, width: 250 }}>
                                                <Grid container direction="column" spacing={1}>
                                                    <Grid item container>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Open positions:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} textAlign="right">
                                                            <Typography variant="body2">
                                                                {Object.keys(analysis.assetPositions).filter(
                                                                    k => analysis.assetPositions[k].totalAmount > 0
                                                                ).length}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item><Box sx={{ borderTop: 1, borderColor: "divider" }} /></Grid>
                                                    <Grid item container>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Transferred in:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} textAlign="right">
                                                            <Typography variant="body2" sx={{ color: transferredIn > 0 ? "success.main" : "text.primary" }}>
                                                                {transferredIn > 0 ? `+${transferredIn.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}` : '–'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item container>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Transferred out:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} textAlign="right">
                                                            <Typography variant="body2" sx={{ color: transferredOut > 0 ? "error.main" : "text.primary" }}>
                                                                {transferredOut > 0 ? `-${transferredOut.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}` : '–'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Card>
                                        </Popover>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>


                {/* Portfolio Charts */}
                <Box pt={2} mt={0} display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
                    <Box flex={2}>
                        <PortfolioChart
                            portfolios={portfolios}
                            currency={currency}
                            isLive={isLive}
                            hasPartialData={hasPartialData}
                            partialDataTooltip={partialDataTooltip}
                        />
                    </Box>
                </Box>
            </CardContent>
        </AppCard>
    );
};
